import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Vault } from "lucide-react";
import clsx from "clsx";
import { useCountUp } from "../../hooks/useCountUp";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as devicesApi from "../../api/devices";
import {
  Device,
  StatoDevice,
  STATO_LABELS,
  TIPO_POSSESSO_LABELS,
  modelloLeggibile,
  relazioniCorrenti,
  StatisticheDevice,
  TipoServizioPrecredit,
  SERVIZI_PRECREDIT,
  SERVIZIO_PRECREDIT_BREVE,
  SERVIZIO_PRECREDIT_LABELS,
  SERVIZIO_PRECREDIT_COLORE,
} from "../../api/devices";
import { Cliente } from "../../api/clients";
import { Table, TableColumn } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { Input } from "../../components/ui/Input";
import { ClienteAutocomplete } from "../../components/ui/ClienteAutocomplete";
import { ExportButton } from "../../components/ui/ExportButton";
import { Button } from "../../components/ui/Button";
import { DeviceNuovo } from "./DeviceNuovo";
import { useAuth } from "../../context/AuthContext";
import { useOrdinamentoUrl, useRicercaUrl, useScriviParametri } from "../../hooks/useStatoUrl";
import { etichettaMatricola } from "../../utils/matricola";

const STATI: StatoDevice[] = ["attivo", "dismesso", "rubato"];
/** Valori grezzi dell'enum: nel pannello export l'utente li vede come sono a database. */
const POSSESSI_FILTRO = ["venduto_evo", "venduto_terzi", "noleggio", "proprieta_cliente"] as const;

/**
 * Gli accessori prendono il verde oliva della palette, l'unico rimasto libero
 * dopo i servizi. Non è un colore "di servizio" e non deve sembrarlo: sta in
 * una riga sua, sotto, con la sua intestazione.
 */
const COLORE_ACCESSORI = "#4d7c0f";

/** Colori delle due tessere di stato, dalla stessa palette dei grafici. */
const STATO_COLORE: Record<"dismesso" | "rubato", string> = {
  dismesso: "#87908b",
  rubato: "#be123c",
};

/**
 * Tessera del riepilogo: stesso linguaggio delle card della Dashboard — barra
 * colorata a sinistra, numero animato, sollevamento al passaggio del mouse — ma
 * è un `button`, non un `Link`: qui il click non porta altrove, filtra la
 * tabella qui sotto. `aria-pressed` lo dice a chi naviga da tastiera, perché
 * "questa tessera è il filtro attivo" altrimenti si vede solo dal colore.
 */
function TesseraRiepilogo({
  valore,
  etichetta,
  titolo,
  colore,
  attiva,
  tratteggiata = false,
  onClick,
}: {
  valore: number;
  etichetta: string;
  titolo: string;
  colore?: string;
  attiva: boolean;
  tratteggiata?: boolean;
  onClick: () => void;
}) {
  const animato = useCountUp(valore);
  const tinta = colore ?? "#87908b";

  return (
    <button
      type="button"
      onClick={onClick}
      title={titolo}
      aria-pressed={attiva}
      style={{
        ...(colore ? { borderLeftColor: colore } : {}),
        ...(attiva ? { outline: `2px solid ${tinta}`, outlineOffset: "1px" } : {}),
      }}
      className={clsx(
        "flex min-w-0 flex-col rounded-card border border-line p-3 text-left transition-[box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:shadow-lift",
        colore ? "border-l-4 bg-white shadow-card" : "border-dashed bg-line/10"
      )}
    >
      <span className="text-xl font-bold tabular-nums" style={{ color: tratteggiata ? undefined : tinta }}>
        <span className={tratteggiata ? "text-ink/60" : undefined}>{animato}</span>
      </span>
      <span className="mt-0.5 truncate text-xs text-ink/50">{etichetta}</span>
    </button>
  );
}

/** Data in forma breve, oppure un trattino: le colonne data sono spesso vuote. */
function dataBreve(iso: string | null) {
  if (!iso) return <span className="text-ink/40">—</span>;
  return <span className="tabular-nums">{new Date(iso).toLocaleDateString("it-IT")}</span>;
}

/** Colore del badge di stato: un device rubato non deve somigliare a uno attivo. */
export function coloreStato(stato: StatoDevice): "success" | "neutral" | "danger" {
  if (stato === "attivo") return "success";
  if (stato === "rubato") return "danger";
  return "neutral";
}

/**
 * Parco device — le singole casseforti installate, una riga per pezzo fisico.
 *
 * Da non confondere con Catalogo Dispositivi, che è l'elenco dei MODELLI con la
 * loro documentazione: lì "Yougo U-ONE 2000E" è una riga sola, qui ce n'è una
 * per ogni esemplare, con la sua matricola e il cliente presso cui si trova.
 *
 * Cliente e sede in tabella vengono dalla relazione CORRENTE (`data_fine` nullo):
 * la colonna risponde a "dov'è oggi", non "dov'è mai stato" — quello è lo
 * storico, e sta nel dettaglio.
 */
export default function Devices() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  // Anticipa la regola della rotta: censire un pezzo è `devices:create`, che è
  // riservato ad admin/staff. Il controllo vero resta lì — questa è solo la
  // ragione per cui il pulsante non compare a chi si vedrebbe rifiutare.
  const puoCensire = hasPermission("devices:create");
  const [nuovoOpen, setNuovoOpen] = useState(false);
  const { showToast } = useToast();

  const [records, setRecords] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<StatisticheDevice | null>(null);

  // Caricate una volta sola e NON al variare dei filtri: il riepilogo racconta
  // com'è messo il parco, non cosa si sta guardando in tabella. Se seguisse i
  // filtri sarebbe un doppione del conteggio righe qui sotto.
  useEffect(() => {
    devicesApi
      .getStatisticheDevice()
      .then(setStats)
      .catch((err) => {
        // Il riepilogo si nasconde quando `stats` è null, quindi un fallimento
        // silenzioso lo farebbe sparire senza distinguerlo da "questa funzione
        // non c'è". Gli altri catch muti di questo frontend degradano elenchi
        // opzionali verso il vuoto, che è già l'informazione giusta; qui invece
        // sparisce un blocco che c'era.
        setStats(null);
        showToast(getErrorMessage(err, "Impossibile caricare il riepilogo del parco."), "error");
      });
    // `showToast` è memoizzato con dipendenze vuote in ToastContext, quindi la
    // sua identità non cambia mai: dichiararlo qui non fa ripartire l'effetto,
    // e vale più di un eslint-disable.
  }, [showToast]);

  // I filtri vivono nell'URL (vedi `hooks/useStatoUrl.ts`): tornando dal
  // dettaglio di un dispositivo si ritrova l'elenco filtrato com'era, e un link
  // copiato apre la stessa vista. `?servizio=`, `?mappa=` e `?cliente_id=`
  // arrivano già così dal widget Wink On Cash di Statistiche; prima erano letti
  // solo all'apertura e poi lo stato andava per conto suo. Un valore fuori
  // elenco viene ignorato invece di essere passato al backend.
  const [searchParams] = useSearchParams();
  const scrivi = useScriviParametri();
  const { ordine, onOrdineChange } = useOrdinamentoUrl();

  const statoUrl = searchParams.get("stato") ?? "";
  const stato: StatoDevice | "" = (STATI as string[]).includes(statoUrl) ? (statoUrl as StatoDevice) : "";
  const servizioUrl = searchParams.get("servizio") ?? "";
  const servizio: TipoServizioPrecredit | "non_specificato" | "" =
    servizioUrl === "non_specificato" || (SERVIZI_PRECREDIT as string[]).includes(servizioUrl)
      ? (servizioUrl as TipoServizioPrecredit | "non_specificato")
      : "";
  const tipo: "accessori" | "" = searchParams.get("tipo") === "accessori" ? "accessori" : "";
  /**
   * Solo le casseforti condivise fra piu' clienti. Booleano e non una stringa
   * come gli altri tre: qui non c'e' un valore da scegliere, la domanda ha una
   * risposta sola.
   */
  const condivisi = searchParams.get("condivisi") === "true";

  /**
   * Drill-down dalle due righe "non sulla mappa" del widget Wink On Cash.
   * Stesso meccanismo di `?servizio=`.
   */
  const mappaUrl = searchParams.get("mappa") ?? "";
  const mappa: "senza_sede" | "sede_non_geolocalizzata" | "" =
    mappaUrl === "senza_sede" || mappaUrl === "sede_non_geolocalizzata" ? mappaUrl : "";

  /**
   * I clienti su cui la mappa era filtrata quando si è cliccato, ripetuti
   * nell'URL come `?cliente_id=a&cliente_id=b`.
   *
   * Sono separati da `clienteId` qui sotto — che è il filtro a singolo cliente
   * dell'autocomplete di questa pagina, in `?cliente=` — perché la mappa ne
   * ammette più di uno e questa UI no. Tenerli separati evita di dover
   * trasformare l'autocomplete in un multi-select solo per non perdere il
   * contesto di provenienza: il filtro arriva, restringe la lista, e si toglie
   * con "Mostra tutti" insieme agli altri.
   */
  const clientiDaMappaChiave = searchParams.getAll("cliente_id").filter(Boolean).join(",");
  const clientiDaMappa = useMemo(
    () => (clientiDaMappaChiave ? clientiDaMappaChiave.split(",") : []),
    [clientiDaMappaChiave]
  );
  /**
   * Il cliente scelto dall'autocomplete. Nell'URL va anche il nome
   * (`?cliente_nome=`), da rimettere nel campo: chi vede i dispositivi non
   * sempre può leggere l'anagrafica clienti (Centri Assistenza, Operatori,
   * Clienti non hanno `clienti_sedi:read`), quindi ricavarlo dall'id non si
   * potrebbe.
   */
  const clienteId = searchParams.get("cliente") ?? "";
  const clienteNomeUrl = searchParams.get("cliente_nome") ?? "";
  const [clienteTesto, setClienteTesto] = useState(clienteNomeUrl);
  useEffect(() => {
    // Riallinea il campo quando il filtro cambia da fuori (Indietro, un link,
    // "Mostra tutti"); il testo digitato senza scegliere un cliente non tocca
    // l'URL, quindi resta dov'è.
    setClienteTesto(clienteNomeUrl);
  }, [clienteId, clienteNomeUrl]);
  const { testo: ricerca, setTesto: setRicerca, valore: ricercaAttiva } = useRicercaUrl();

  useEffect(() => {
    let annullato = false;
    setIsLoading(true);
    devicesApi
      .listDevices({
        stato,
        servizio,
        tipo,
        mappa,
        // Il filtro della pagina vince su quello arrivato dalla mappa: se
        // l'utente sceglie un cliente dall'autocomplete sta restringendo a
        // mano, e sovrapporre i due darebbe un'intersezione che non ha chiesto.
        cliente_id: clienteId || clientiDaMappa,
        q: ricercaAttiva,
        solo_correnti: true,
        condivisi,
      })
      .then((d) => {
        if (!annullato) setRecords(d.records);
      })
      .catch((err) => {
        if (!annullato) showToast(getErrorMessage(err, "Impossibile caricare i dispositivi."), "error");
      })
      .finally(() => {
        if (!annullato) setIsLoading(false);
      });
    return () => {
      annullato = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stato, servizio, tipo, mappa, clienteId, clientiDaMappa, ricercaAttiva, condivisi]);

  /**
   * Le tessere fanno da interruttore: ricliccare quella attiva toglie il
   * filtro. Servizio e stato si escludono a vicenda perché il riepilogo per
   * servizio conta solo i device attivi — tenerli insieme produrrebbe sempre
   * zero righe (nessun device è insieme attivo e dismesso).
   */
  function applicaServizio(s: TipoServizioPrecredit | "non_specificato") {
    scrivi({ stato: null, tipo: null, condivisi: null, servizio: servizio === s ? null : s });
  }

  function applicaStato(s: StatoDevice) {
    scrivi({ servizio: null, tipo: null, condivisi: null, stato: stato === s ? null : s });
  }

  /**
   * Gli accessori si escludono dagli altri due come stato e servizio si
   * escludono fra loro: il conteggio per servizio non li comprende, quindi
   * tenerli insieme darebbe sempre zero righe.
   */
  function applicaAccessori() {
    scrivi({ servizio: null, stato: null, condivisi: null, tipo: tipo === "accessori" ? null : "accessori" });
  }

  /**
   * Spegne gli altri tre come fanno loro fra di loro, e per lo stesso motivo:
   * la tessera promette un numero e l'elenco che apre deve avere quelle righe.
   * Sommandoci un altro filtro il conteggio resterebbe a schermo mentre la
   * tabella mostra un sottoinsieme — che e' esattamente il difetto chiuso con
   * la #95, li' per una clausola dimenticata invece che per due filtri sommati.
   */
  function applicaCondivisi() {
    scrivi({ servizio: null, stato: null, tipo: null, condivisi: condivisi ? null : "true" });
  }

  const columns = useMemo<TableColumn<Device>[]>(
    () => [
      {
        key: "matricola",
        header: "Matricola",
        // Non si spegne: e' l'identificativo del pezzo fisico, e una tabella
        // del parco senza matricola non dice piu' di quale cassaforte parla.
        // Aggiunta dopo la #97, insieme alle stesse colonne obbligatorie di
        // Clienti, Sedi e Registro attivita'.
        sempreVisibile: true,
        sortValue: (r) => r.matricola,
        render: (r) => <span className="font-semibold text-ink">{etichettaMatricola(r.matricola)}</span>,
      },
      {
        key: "cliente",
        header: "Cliente attuale",
        // Si ordina sul primo cliente corrente, lo stesso che la cella mostra
        // per esteso: ordinare su un valore che non si vede confonderebbe.
        sortValue: (r) => relazioniCorrenti(r)[0]?.cliente.nome ?? null,
        render: (r) => {
          const correnti = relazioniCorrenti(r);
          if (correnti.length === 0) return <span className="text-ink/40">Nessuno</span>;
          // Una cassaforte può servire più clienti insieme: mostrarne uno solo
          // farebbe sparire dalla vista proprio i device condivisi, che sono il
          // caso per cui la relazione è una tabella e non una colonna.
          return (
            <span title={correnti.map((c) => c.cliente.nome).join(" · ")}>
              {correnti[0].cliente.nome}
              {correnti.length > 1 && (
                <span className="ml-1 rounded bg-black/5 px-1.5 py-0.5 text-xs font-semibold text-ink/60">
                  +{correnti.length - 1}
                </span>
              )}
            </span>
          );
        },
      },
      {
        key: "sede",
        header: "Sede",
        sortValue: (r) => relazioniCorrenti(r).find((c) => c.sede_cliente)?.sede_cliente?.nome_sede ?? null,
        render: (r) => {
          const sedi = relazioniCorrenti(r)
            .map((c) => c.sede_cliente)
            .filter((s): s is NonNullable<typeof s> => s !== null);
          if (sedi.length === 0) return <span className="text-ink/40">—</span>;
          return (
            <span title={sedi.map((s) => s.nome_sede ?? "Sede senza nome").join(" · ")}>
              {sedi[0].nome_sede ?? "Sede senza nome"}
              {sedi[0].citta && <span className="text-ink/50"> · {sedi[0].citta}</span>}
              {sedi.length > 1 && (
                <span className="ml-1 rounded bg-black/5 px-1.5 py-0.5 text-xs font-semibold text-ink/60">
                  +{sedi.length - 1}
                </span>
              )}
            </span>
          );
        },
      },
      {
        key: "modello",
        header: "Modello",
        sortValue: (r) => modelloLeggibile(r),
        render: (r) => {
          const m = modelloLeggibile(r);
          if (!m) return <span className="text-ink/40">—</span>;
          // Senza corrispondenza a catalogo il modello è la stringa grezza
          // della fonte: si mostra lo stesso, ma detto che non è riconciliata.
          return r.dispositivo_catalogo ? (
            <span>{m}</span>
          ) : (
            <span className="text-ink/70" title="Modello non riconciliato con il Catalogo Dispositivi">
              {m} <span className="text-ink/35">(non a catalogo)</span>
            </span>
          );
        },
      },
      {
        key: "stato",
        header: "Stato",
        // Sull'etichetta e non sull'enum: si ordina per quello che si legge.
        sortValue: (r) => STATO_LABELS[r.stato],
        render: (r) => <Badge color={coloreStato(r.stato)}>{STATO_LABELS[r.stato]}</Badge>,
      },
      {
        key: "tipo_possesso",
        header: "Possesso",
        sortValue: (r) => (r.tipo_possesso ? TIPO_POSSESSO_LABELS[r.tipo_possesso] : null),
        render: (r) =>
          r.tipo_possesso ? (
            TIPO_POSSESSO_LABELS[r.tipo_possesso]
          ) : (
            // Nullable di proposito: per gran parte del parco storico il dato
            // non esiste in nessuna fonte. "Non noto" è diverso da vuoto.
            <span className="text-ink/40">Non noto</span>
          ),
      },

      // --- Colonne spente di default ----------------------------------------
      // Esistono ma non allargano la tabella per tutti: si accendono dal menu
      // "Colonne", e la scelta resta salvata. Sono i campi che servono a chi sta
      // facendo un lavoro specifico (censimento date, verifica contratti,
      // riconciliazione GDO), non a chi apre la pagina per cercare una matricola.
      {
        key: "data_installazione",
        header: "Installazione",
        nascostaDiDefault: true,
        // Si ordina sulla stringa ISO, non sulla data formattata: "01/12/2025"
        // e "02/01/2026" in ordine alfabetico finirebbero al contrario.
        sortValue: (r) => r.data_installazione,
        render: (r) => dataBreve(r.data_installazione),
      },
      {
        key: "data_attivazione",
        header: "Attivazione",
        nascostaDiDefault: true,
        sortValue: (r) => r.data_attivazione,
        render: (r) => dataBreve(r.data_attivazione),
      },
      {
        key: "centro_installazione",
        header: "Centro installazione",
        nascostaDiDefault: true,
        sortValue: (r) => r.centro_assistenza_installazione?.nome ?? null,
        render: (r) =>
          r.centro_assistenza_installazione?.nome ?? <span className="text-ink/40">—</span>,
      },
      {
        key: "servizio_precredit",
        header: "Servizio precredit",
        nascostaDiDefault: true,
        // Dalla relazione CORRENTE, come la colonna Cliente: il servizio è un
        // fatto della coppia device↔cliente, non del pezzo.
        sortValue: (r) => {
          const s = relazioniCorrenti(r)[0]?.tipo_servizio_precredit;
          return s ? SERVIZIO_PRECREDIT_BREVE[s] : null;
        },
        render: (r) => {
          const correnti = relazioniCorrenti(r);
          const s = correnti[0]?.tipo_servizio_precredit;
          if (!s) {
            // "Non specificato" è una voce a sé nel riepilogo, non un'assenza
            // qualsiasi: qui si legge allo stesso modo.
            return <span className="text-ink/40">{correnti.length > 0 ? "Non specificato" : "—"}</span>;
          }
          return (
            <span title={SERVIZIO_PRECREDIT_LABELS[s]}>
              {SERVIZIO_PRECREDIT_BREVE[s]}
              {correnti.length > 1 && (
                <span className="ml-1 rounded bg-black/5 px-1.5 py-0.5 text-xs font-semibold text-ink/60">
                  +{correnti.length - 1}
                </span>
              )}
            </span>
          );
        },
      },
      {
        key: "codice_gdo",
        header: "Codice GDO",
        nascostaDiDefault: true,
        sortValue: (r) => relazioniCorrenti(r).find((c) => c.codice_gdo)?.codice_gdo ?? null,
        render: (r) => {
          const codici = relazioniCorrenti(r)
            .map((c) => c.codice_gdo)
            .filter((c): c is string => !!c);
          if (codici.length === 0) return <span className="text-ink/40">—</span>;
          return <span className="tabular-nums">{codici.join(" · ")}</span>;
        },
      },
    ],
    []
  );

  return (
    <div>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Vault className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink">Dispositivi</h1>
            <p className="mt-1 text-sm text-ink/50">
              Le casseforti installate, una riga per pezzo fisico. I modelli e la loro documentazione stanno in Catalogo
              Dispositivi.
            </p>
          </div>
        </div>

        {/* Stesso componente condiviso degli altri moduli: i filtri qui sotto
            sono quelli che l'endpoint di export già interpreta, gli stessi
            dell'elenco. */}
        <ExportButton
          endpoint="/devices/export"
          filename="dispositivi.xlsx"
          fields={[
            { key: "stato", label: "Stato", type: "select", options: ["attivo", "dismesso", "rubato"] },
            {
              key: "servizio",
              label: "Servizio precredit",
              type: "select",
              options: [...SERVIZI_PRECREDIT, "non_specificato"],
            },
            { key: "tipo_possesso", label: "Tipo di possesso", type: "select", options: [...POSSESSI_FILTRO] },
            { key: "q", label: "Matricola contiene", type: "text" },
          ]}
          // Il filtro "condivisi" non ha un campo nel pannello — non è un valore
          // da scegliere ma un interruttore già acceso qui — quindi viaggia come
          // filtro della vista, la stessa strada dei clienti arrivati dalla
          // mappa. Spento non manda niente: `extraParams` scarta le stringhe
          // vuote. L'export lo interpreta come l'elenco, così il foglio non
          // contraddice la tabella da cui si è partiti.
          extraParams={{ condivisi: condivisi ? "true" : "" }}
        />

        {/* Censimento di un pezzo, indipendente da installazione e attivazione.
            La rotta `POST /api/devices` esisteva da sempre e nessuna pagina la
            chiamava: l'unico modo di far comparire un device era salvare
            un'installazione, e non funzionava nemmeno quello. */}
        {puoCensire && (
          <Button type="button" onClick={() => setNuovoOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Nuovo dispositivo
          </Button>
        )}
      </header>

      <DeviceNuovo
        open={nuovoOpen}
        onClose={() => setNuovoOpen(false)}
        // Alla scheda del pezzo appena censito: da lì si completano i dati che
        // il form non chiede (assistenza, noleggio, servizio precredit).
        onCreato={(id) => navigate(`/devices/${id}`)}
      />

      {stats && (
        <section className="mb-6" aria-label="Riepilogo del parco">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">
              Servizio precredit — relazioni in corso su device attivi
            </p>
            {(servizio || stato || tipo || condivisi || mappa || clientiDaMappa.length > 0) && (
              <button
                type="button"
                onClick={() =>
                  scrivi({ servizio: null, stato: null, tipo: null, condivisi: null, mappa: null, cliente_id: null })
                }
                className="text-xs font-semibold text-primary hover:underline"
              >
                Mostra tutti
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {SERVIZI_PRECREDIT.map((s) => (
              <TesseraRiepilogo
                key={s}
                valore={stats.per_servizio[s] ?? 0}
                etichetta={SERVIZIO_PRECREDIT_BREVE[s]}
                titolo={SERVIZIO_PRECREDIT_LABELS[s]}
                colore={SERVIZIO_PRECREDIT_COLORE[s]}
                attiva={servizio === s}
                onClick={() => applicaServizio(s)}
              />
            ))}
            {/* Tratteggiata e senza colore proprio, distinta dalle altre cinque:
                non è un servizio, è la misura di quanto non sappiamo ancora.
                Tenerla dentro "Nessun Collegamento Banca" avrebbe fatto sparire
                il lavoro di verifica che resta da fare. */}
            <TesseraRiepilogo
              valore={stats.servizio_non_specificato}
              etichetta="Non specificato"
              titolo="Relazioni in corso per cui il servizio non è stato indicato"
              attiva={servizio === "non_specificato"}
              tratteggiata
              onClick={() => applicaServizio("non_specificato")}
            />
          </div>

          <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-ink/45">Device fuori servizio</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
            <TesseraRiepilogo
              valore={stats.per_stato.dismesso ?? 0}
              etichetta="Dismessi"
              titolo="Device usciti di servizio"
              colore={STATO_COLORE.dismesso}
              attiva={stato === "dismesso"}
              onClick={() => applicaStato("dismesso")}
            />
            <TesseraRiepilogo
              valore={stats.per_stato.rubato ?? 0}
              etichetta="Rubati"
              titolo="Device dichiarati rubati"
              colore={STATO_COLORE.rubato}
              attiva={stato === "rubato"}
              onClick={() => applicaStato("rubato")}
            />
          </div>

          {/* --- Condivise ---------------------------------------------------
              Riga a sé, come gli Accessori: non è un servizio né uno stato, è
              una proprietà della relazione — quante insegne versano nello
              stesso pezzo. Serve perché su una cassaforte condivisa "il
              cliente" non è una risposta sola, e finora quei device si
              trovavano solo scorrendo l'elenco a occhio in cerca del +1
              accanto al nome.

              Compare solo se ce n'è almeno una: a zero, una tessera spenta
              inviterebbe a cliccare su un elenco vuoto. */}
          {stats.condivisi > 0 && (
            <>
              <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-ink/45">
                Condivise fra più clienti
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
                <TesseraRiepilogo
                  valore={stats.condivisi}
                  etichetta="Condivise"
                  titolo="Più di una relazione cliente aperta sullo stesso pezzo fisico"
                  attiva={condivisi}
                  tratteggiata
                  onClick={applicaCondivisi}
                />
              </div>
            </>
          )}

          {/* --- Accessori ---------------------------------------------------
              Riga a sé e non una settima tessera fra i servizi: un
              concentratore non è un servizio che non sappiamo, è un pezzo per
              cui il servizio non esiste. Messo lì dentro si leggerebbe come
              lavoro di verifica ancora da fare, che è esattamente il malinteso
              da cui nasce questa sezione. */}
          {stats.accessori > 0 && (
            <>
              <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-ink/45">
                Accessori — senza servizio Pre Credit
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
                <TesseraRiepilogo
                  valore={stats.accessori}
                  etichetta="Concentratori"
                  titolo="Apparati di rete: non custodiscono contante e non hanno un servizio Pre Credit"
                  colore={COLORE_ACCESSORI}
                  attiva={tipo === "accessori"}
                  onClick={applicaAccessori}
                />
              </div>
            </>
          )}
        </section>
      )}

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input
          label="Cerca matricola"
          placeholder="es. UB190001"
          value={ricerca}
          onChange={(e) => setRicerca(e.target.value)}
        />

        <ClienteAutocomplete
          label="Cliente"
          value={clienteTesto}
          onChange={(v) => {
            setClienteTesto(v);
            // Svuotare il campo toglie il filtro: senza questo, cancellare il
            // testo lascerebbe attivo un filtro invisibile.
            if (!v.trim()) scrivi({ cliente: null, cliente_nome: null });
          }}
          onSelect={(c: Cliente) => {
            setClienteTesto(c.nome);
            scrivi({ cliente: c.id, cliente_nome: c.nome });
          }}
        />

        <div>
          <label htmlFor="filtro-stato" className="mb-1 block text-sm font-medium text-ink">
            Stato
          </label>
          <select
            id="filtro-stato"
            value={stato}
            onChange={(e) => scrivi({ stato: e.target.value })}
            className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">Tutti</option>
            {STATI.map((s) => (
              <option key={s} value={s}>
                {STATO_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Table
        columns={columns}
        ordinabile
        ordine={ordine}
        onOrdineChange={onOrdineChange}
        sceltaColonne="devices"
        data={records}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="Nessun dispositivo."
        onRowClick={(r) => navigate(`/devices/${r.id}`)}
      />

      {!isLoading && records.length > 0 && (
        <p className="mt-3 text-xs text-ink/45">
          {records.length} {records.length === 1 ? "dispositivo" : "dispositivi"}
        </p>
      )}
    </div>
  );
}
